import { useEffect } from "react";
import type { ReactNode } from "react";
import { useUserStore } from "@/store/useUserStore";
import { fetchUserProfile } from "@/api/user";

/**
 * 达人身份预取守卫（轻量）
 *
 * 与 WxAuthGuard 的区别：
 *   - 【不做】登录校验 / 未登录跳转微信授权
 *   - 【不做】工作台路由拦截 / config 预加载
 *   - 【只做】已登录时预取 is_talent，让首页底部导航能立即正确判断是否显示「工作台」
 *
 * 用于首页（/home）这类允许游客访问、但需要按身份渲染的页面。
 */

/** 从 localStorage 直接读取 token（兜底 Zustand persist 未及时恢复的情况） */
function getLocalToken(): string | null {
  try {
    const raw = localStorage.getItem("user-storage");
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed.state?.token ?? parsed.token ?? null;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** 全局单例：防止多组件/多次渲染重复请求 profile */
let talentPrefetchPromise: Promise<void> | null = null;

export default function TalentPrefetch({ children }: { children: ReactNode }) {
  const isLoggedIn = useUserStore((state) => state.isLoggedIn);
  const isTalent = useUserStore((state) => state.isTalent);
  const setIsTalent = useUserStore((state) => state.setIsTalent);

  // 登录判定（与 WxAuthGuard 保持一致的口径）
  const hasLocalToken = !isLoggedIn && !!getLocalToken();
  const isAuthenticated = isLoggedIn || hasLocalToken;

  useEffect(() => {
    // 未登录 → 不请求（游客只看游客视图，不发达人接口）
    if (!isAuthenticated) return;
    // store 中已是达人 → 无需重复请求
    if (isTalent === 1) return;

    if (!talentPrefetchPromise) {
      talentPrefetchPromise = fetchUserProfile()
        .then((data) => {
          // ★ 只信任接口返回值，非 1 一律按 0 处理（安全方向，绝不误显示工作台）
          const talent = (data.is_talent ?? data.user?.is_talent) === 1 ? 1 : 0;
          setIsTalent(talent);
          // 同步写入独立缓存，确保 UserBottomNav 在任何入口都能正确读取
          localStorage.setItem("is_talent_cache", String(talent));
        })
        .catch(() => {
          // ★ 接口失败 → 显式写 0（非达人），静默处理，绝不误显示工作台
          setIsTalent(0);
          localStorage.setItem("is_talent_cache", "0");
        })
        .finally(() => {
          // 延迟清除，允许短时间内复用结果
          setTimeout(() => {
            talentPrefetchPromise = null;
          }, 3000);
        });
    }
  }, [isAuthenticated, isTalent, setIsTalent]);

  // 透传渲染，不阻塞、不拦截
  return <>{children}</>;
}
